import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Palette, Check, ChevronDown } from "lucide-react";
import { THEME_OPTIONS, type ThemeOption } from "@/components/ui/page-background";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { NotificationSettings } from "@/components/notification-settings";
import { PushNotificationTest } from "@/components/push-notification-test";

interface SettingsModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function SettingsModal({ isOpen, onOpenChange }: SettingsModalProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [multiScreenJams, setMultiScreenJams] = useState(user?.multiScreenJams || false);
  const [currentTheme, setCurrentTheme] = useState<string>(user?.theme || "ocean");
  const [isThemePopoverOpen, setIsThemePopoverOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  useEffect(() => {
    setMultiScreenJams(user?.multiScreenJams || false);
    setCurrentTheme(user?.theme || "ocean");
  }, [user?.multiScreenJams, user?.theme]);

  const updateUserSettingsMutation = useMutation({
    mutationFn: async (settings: { multiScreenJams?: boolean }) => {
      return apiRequest("PATCH", `/api/users/${user?.id}/settings`, settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-user"] });
      toast({
        title: "Settings saved",
        description: "Your preferences have been updated.",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateThemeMutation = useMutation({
    mutationFn: async (theme: string) => {
      const response = await fetch("/api/users/theme", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme }),
        credentials: 'include',
      });
      if (!response.ok) throw new Error("Failed to update theme");
    },
    onSuccess: () => {
      toast({
        title: "Theme updated",
        description: "Your theme has been changed.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update theme. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateUserSettingsMutation.mutate({ multiScreenJams });
  };

  const handleThemeSelect = (themeId: string) => {
    setCurrentTheme(themeId);
    setIsThemePopoverOpen(false);
    
    // Update localStorage immediately before API call to ensure persistence
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        userData.theme = themeId;
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Dispatch event to notify other components of user settings change
        window.dispatchEvent(new Event('user-settings-changed'));
      } catch (error) {
        console.error('Error updating user theme in localStorage:', error);
      }
    }
    
    // Save to user account
    updateThemeMutation.mutate(themeId);
    
    // Force page reload to apply theme change
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const currentThemeOption = THEME_OPTIONS.find(option => option.id === currentTheme) || THEME_OPTIONS[0];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>User Settings</DialogTitle>
          <DialogDescription>
            Manage your application preferences.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          {/* Push Notifications - Collapsible */}
          <Collapsible
            open={isNotificationOpen}
            onOpenChange={setIsNotificationOpen}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between p-4 hover:bg-white/30 backdrop-blur-sm border border-white/20 rounded-xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <span className="text-base font-medium">Push Notifications</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isNotificationOpen ? "rotate-180" : ""
                  }`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <NotificationSettings />
              
              {/* Push Notification Test (for development) */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-4">
                  <PushNotificationTest />
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
          {/* Multi-Screen Jams Setting */}
          <div className="flex items-center justify-between space-x-2">
            <Label htmlFor="multi-screen-jams" className="flex flex-col">
              <span>Multi-Screen Jams</span>
              <span className="text-sm text-muted-foreground">Enable a tabbed layout for jam nights.</span>
            </Label>
            <Switch
              id="multi-screen-jams"
              checked={multiScreenJams}
              onCheckedChange={setMultiScreenJams}
            />
          </div>

          {/* Theme Setting */}
          <div className="flex items-center justify-between space-x-2">
            <Label className="flex flex-col">
              <span>Theme</span>
              <span className="text-sm text-muted-foreground">Choose your preferred color theme.</span>
            </Label>
            <div className="flex items-center justify-between">
              <Popover open={isThemePopoverOpen} onOpenChange={setIsThemePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                    disabled={updateThemeMutation.isPending}
                  >
                    <Palette className="h-4 w-4 mr-2" />
                    {currentThemeOption.name}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-48 bg-white/20 backdrop-blur-xl border-white/30 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                  }}
                >
                  <div className="space-y-1">
                    {THEME_OPTIONS.map((theme: ThemeOption) => (
                      <button
                        key={theme.id}
                        onClick={() => handleThemeSelect(theme.id)}
                        disabled={updateThemeMutation.isPending}
                        className={`w-full p-2 text-left rounded-lg transition-all duration-200 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed ${
                          currentTheme === theme.id ? 'bg-white/30' : 'bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900/90 drop-shadow-sm">{theme.name}</span>
                          {currentTheme === theme.id && (
                            <Check className="h-4 w-4 text-blue-600 drop-shadow-sm" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={updateUserSettingsMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={updateUserSettingsMutation.isPending}
            className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
            style={{
              boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            {updateUserSettingsMutation.isPending ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
